// Which notices Raw Data owes the reader, as one-line decisions.
//
// The page used to stack up to three paragraph Alerts above the tabs, so the
// data — the thing the page is named after — started below the fold, and the
// banner counts duplicated the chips already rendered beside the Rows toggle.
// The facts stay, compressed to a thin strip; this module decides which
// notices exist, their severity and their text, so the strip renders whatever
// it says and the tests read the decisions rather than the markup.

const group = (n: number) => n.toLocaleString('en-IN')

export interface NoticeBadge {
  label: string
  color: 'yellow' | 'green' | 'gray'
  /** filled while the count is still somebody's work */
  filled: boolean
}

export interface RawNotice {
  key: 'books_dups' | 'a26_repeats' | 'verdicts_dropped'
  color: 'yellow' | 'green' | 'gray'
  text: string
  badges: NoticeBadge[]
}

interface EntryLike {
  side: string
  duplicates?: number | null
  dup_unreviewed?: number | null
  dup_genuine?: number | null
  dup_not_duplicate?: number | null
  dup_reviewable?: boolean | null
}

/** The title-row chip (density pass A1): one glanceable summary of everything
 *  the notice strip says, so the strip itself can live in a popover instead
 *  of two full-width bars above the data.
 *
 *  The colour is the workflow signal and must stay visible without a click:
 *  yellow while any duplicate group is unreviewed or any carried verdict was
 *  lost (both gate runs under a Block policy), green when the judging is
 *  done, gray when there is only unanswerable 26AS information. Null when
 *  there is nothing to say at all. */
export function noticeChip(
  entries: EntryLike[],
  carriedVerdicts?: { carried: number; dropped: unknown[] } | null,
): { label: string; color: 'yellow' | 'green' | 'gray' } | null {
  // BOOKS only. 26AS repeats are the deductor's own filing: not processed,
  // and — user decision, 13-Aug — not mentioned either.
  const reviewable = entries.filter((e) => e.dup_reviewable !== false)
  const dups = reviewable.reduce((a, e) => a + (e.duplicates ?? 0), 0)
  const unrev = reviewable.reduce((a, e) => a + (e.dup_unreviewed ?? 0), 0)
  const lost = carriedVerdicts?.dropped?.length ?? 0
  const parts: string[] = []
  if (dups > 0) parts.push(`${group(dups)} dup?`)
  if (lost > 0) parts.push(`${group(lost)} to re-judge`)
  if (!parts.length) return null
  const color = unrev > 0 || lost > 0 ? 'yellow' : 'green'
  return { label: parts.join(' · '), color }
}

/** The strip's contents. Sides stay apart, deliberately: only the books can
 *  be judged, and a combined figure would put thousands of unanswerable 26AS
 *  repeats into the same sentence as the rows somebody actually owes. */
export function rawNotices(
  entries: EntryLike[],
  dupPolicy: 'warn' | 'block' | string,
  carriedVerdicts?: { carried: number; dropped: unknown[] } | null,
): RawNotice[] {
  const out: RawNotice[] = []
  const lost = carriedVerdicts?.dropped?.length ?? 0
  if (lost > 0) {
    out.push({
      key: 'verdicts_dropped',
      color: 'yellow',
      text: `${group(lost)} earlier duplicate verdict${lost === 1 ? '' : 's'} `
        + 'could not carry to the re-uploaded books — re-judge those groups'
        + (dupPolicy === 'block' ? ' before the next run (policy is Block).' : '.'),
      badges: [],
    })
  }
  const reviewable = entries.filter((e) => e.dup_reviewable !== false)
  const frozen = entries.filter((e) => e.dup_reviewable === false)
  const dups = reviewable.reduce((a, e) => a + (e.duplicates ?? 0), 0)
  const unrev = reviewable.reduce((a, e) => a + (e.dup_unreviewed ?? 0), 0)
  const gen = reviewable.reduce((a, e) => a + (e.dup_genuine ?? 0), 0)
  const notdup = reviewable.reduce((a, e) => a + (e.dup_not_duplicate ?? 0), 0)
  if (dups > 0) {
    out.push({
      key: 'books_dups',
      color: unrev > 0 ? 'yellow' : 'green',
      text: `${group(dups)} possible duplicate${dups === 1 ? '' : 's'} in the `
        + 'books — judge each with the dup? menu; nothing is deleted either way.'
        + (dupPolicy === 'block' && unrev > 0
          ? ' Policy is Block: new runs are refused until every group is judged.'
          : ''),
      badges: [
        { label: `${group(unrev)} unreviewed`, color: 'yellow', filled: unrev > 0 },
        { label: `${group(gen)} genuine`, color: 'green', filled: false },
        { label: `${group(notdup)} not a duplicate`, color: 'gray', filled: false },
      ],
    })
  }
  // 26AS repeats (the `frozen` entries) are deliberately NOT reported: the
  // deductor's own filing is not processed, and — user decision, 13-Aug —
  // not mentioned either. Reversals still net automatically.
  void frozen
  return out
}
